<template>
  <Teleport to="body">
    <div class="import-backdrop" @mousedown.self="close">
      <section ref="dialog" class="import-dialog purchases-typography" role="dialog" aria-modal="true" aria-labelledby="purchase-import-title" tabindex="-1" @keydown.esc.stop.prevent="close" @keydown.tab="trapFocus">
        <header><h2 id="purchase-import-title">Add to Purchases</h2><button class="close-button" type="button" aria-label="Close purchase" :disabled="saving" @click="close">×</button></header>
        <p class="source-description">{{ item.description }}</p>
        <p v-if="loading" role="status">Preparing purchase details…</p>
        <p v-if="error" role="alert">{{ error }}</p>
        <button v-if="!loading && !draft" class="noc-back-btn" type="button" @click="prepare">Try again</button>
        <form v-if="draft" @submit.prevent="save">
          <p v-if="warnings.length" class="review-note">{{ warnings.join(' ') }}</p>
          <fieldset :disabled="saving" class="fields">
            <label>Date<input v-model="draft.date" type="date" required /></label>
            <label>Status<select v-model="draft.documentStatus"><option v-for="status in statuses" :key="status" :value="status">{{ status || 'Not set' }}</option></select></label>
            <label>Vendor<CatalogSearchInput v-model="draft.vendorId" :options="catalogs.vendors" placeholder="Select vendor" /></label>
            <label>COS<CatalogSearchInput v-model="draft.cosId" :options="catalogs.costs" placeholder="Select COS" /></label>
            <label class="full-width">Building<CatalogSearchInput v-model="draft.buildingId" :options="catalogs.buildings" placeholder="Select building" /></label>
            <div class="amounts full-width">
              <label>Gross (£)<input :value="draft.gross" type="number" step="0.01" required @input="updateAmount('gross', $event.target.value)" /></label>
              <label>Net (£)<input :value="draft.net" type="number" step="0.01" @input="updateAmount('net', $event.target.value)" /></label>
              <div class="vat-field">
                <label>VAT (£)<input :value="draft.vat" type="number" step="0.01" @input="updateAmount('vat', $event.target.value)" /></label>
                <button type="button" class="vat-toggle-btn" :aria-label="`Change VAT rate, currently ${vatRate}%`" @click="toggleVatRate">{{ vatRate }}%</button>
              </div>
            </div>
            <label class="full-width">Notes<textarea v-model="draft.notes" rows="3" maxlength="2000" /></label>
          </fieldset>
          <footer><button class="noc-back-btn" type="button" :disabled="saving" @click="close">Cancel</button><button class="noc-back-btn primary" type="submit" :disabled="saving">{{ saving ? 'Adding…' : 'Add to Purchases' }}</button></footer>
        </form>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import { PURCHASE_DOCUMENT_STATUSES as statuses } from '@/constants/purchaseStatuses';
import { prepareInvoicePurchase, importInvoicePurchase } from '@/services/invoicePurchases';
import { PURCHASE_VAT_RATES, DEFAULT_PURCHASE_VAT_RATE, inferPurchaseVatRate, recalculatePurchaseAmounts } from '@/utils/purchaseAmounts';

const props = defineProps({ invoiceId: { type: [String, Number], required: true }, index: { type: Number, required: true }, item: { type: Object, required: true } });
const emit = defineEmits(['close', 'added']);
const dialog = ref(null), loading = ref(true), saving = ref(false), error = ref('');
const draft = ref(null), catalogs = ref({ vendors: [], costs: [], buildings: [] }), warnings = ref([]);
const vatRate = ref(DEFAULT_PURCHASE_VAT_RATE);
const emptyAmount = (value) => value == null || String(value).trim() === '';
const recalculate = (field) => recalculatePurchaseAmounts(draft.value, field, vatRate.value);
const updateAmount = (field, value) => {
  draft.value[field] = value;
  recalculate(field);
};
const toggleVatRate = () => {
  vatRate.value = PURCHASE_VAT_RATES[(PURCHASE_VAT_RATES.indexOf(vatRate.value) + 1) % PURCHASE_VAT_RATES.length];
  const field = ['gross', 'net', 'vat'].find((key) => !emptyAmount(draft.value[key]));
  if (field) recalculate(field);
};
let fingerprint = '', disposed = false;
const previousFocus = document.activeElement;
const close = () => { if (!saving.value) emit('close'); };
const prepare = async () => {
  loading.value = true; error.value = '';
  try {
    const result = await prepareInvoicePurchase(props.invoiceId, props.index);
    if (disposed) return;
    if (result.alreadyAdded) { emit('added', result); return; }
    draft.value = { ...result.draft, documentStatus: result.draft.documentStatus || '✓' };
    vatRate.value = inferPurchaseVatRate(draft.value);
    if (!emptyAmount(draft.value.gross) && (emptyAmount(draft.value.net) || emptyAmount(draft.value.vat))) recalculate('gross');
    catalogs.value = result.catalogs;
    warnings.value = (result.warnings || []).filter((warning) =>
      warning !== 'The invoice does not provide a confirmed Net / VAT breakdown. These fields are left blank.'
      || emptyAmount(draft.value.net) || emptyAmount(draft.value.vat));
    fingerprint = result.fingerprint;
  } catch (err) { if (!disposed) error.value = err.message; }
  finally { if (!disposed) loading.value = false; }
};
const save = async () => {
  if (saving.value) return;
  saving.value = true; error.value = '';
  try {
    const result = await importInvoicePurchase(props.invoiceId, props.index, { fingerprint, purchase: { ...draft.value } });
    if (!disposed) emit('added', result);
  } catch (err) { if (!disposed) error.value = err.message; }
  finally { if (!disposed) saving.value = false; }
};
const trapFocus = (event) => {
  const nodes = [...dialog.value.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')];
  if (!nodes.length) { event.preventDefault(); return; }
  const first = nodes[0], last = nodes[nodes.length - 1];
  if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.value)) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.value)) { event.preventDefault(); first.focus(); }
};
onMounted(async () => { await nextTick(); dialog.value?.focus(); void prepare(); });
onBeforeUnmount(() => { disposed = true; previousFocus?.focus?.(); });
</script>

<style scoped>
.import-backdrop { position: fixed; inset: 0; z-index: 2000; display: grid; place-items: center; padding: 24px; background: rgb(0 0 0 / 35%); }
.import-dialog { width: min(640px, 100%); max-height: 90vh; overflow-y: auto; box-sizing: border-box; padding: 24px; border-radius: 12px; background: var(--noc-background, #fff); color: var(--text-color); font: var(--ap-type-body); }
header, footer { display: flex; align-items: center; gap: 12px; }
header { justify-content: space-between; }
h2 { margin: 0; font: var(--ap-type-modal-title); }
.close-button { border: 0; background: transparent; padding: 4px 8px; color: inherit; font-size: 24px; cursor: pointer; }
.source-description { margin: 12px 0 20px; }
.review-note { color: var(--text-color-secondary); font: var(--ap-type-caption); margin: 0 0 16px; }
[role="alert"] { color: #a4332a; }
.fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; border: 0; padding: 0; margin: 0; min-width: 0; }
label { display: flex; flex-direction: column; gap: 6px; min-width: 0; font: var(--ap-type-control); }
.full-width { grid-column: 1 / -1; }
.amounts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.vat-field { position: relative; }
.vat-field input { padding-right: 52px; }
.vat-toggle-btn { position: absolute; right: 6px; bottom: 6px; border: 0; border-radius: 4px; padding: 4px; color: var(--text-color-secondary); background: var(--noc-background-secondary); font: var(--ap-type-label); cursor: pointer; }
.vat-toggle-btn:hover { background-color: color-mix(in srgb, var(--text-color-secondary) 8%, transparent); }
input, select, textarea, :deep(.catalog-search-input .field-input) { box-sizing: border-box; width: 100%; min-width: 0; border: 1px solid var(--border-color); border-radius: 6px; padding: 9px 12px; background: var(--noc-background, #fff); color: var(--text-color); font: var(--ap-type-body); }
input:focus, select:focus, textarea:focus { outline: none; border-color: var(--noc-blue); }
textarea { resize: vertical; }
footer { justify-content: flex-end; margin-top: 24px; }
.primary { background: var(--noc-blue); color: #fff; }
@media (max-width: 540px) { .import-backdrop { padding: 12px; } .import-dialog { padding: 16px; } .fields { grid-template-columns: 1fr; } .amounts { gap: 6px; } }
</style>
